import React, { useContext } from 'react';

import { PlaceOrderButton } from '../PlaceOrderButton';
import { Protection } from '../Protection';
import { CostAndCharges } from '../CostAndCharges';
import { PurchaseDetails } from '../PurchaseDetails';

import * as Styled from './styled';
import { TradingContext, TVariation } from '@trader/contexts';
import { calculateSpread, formatByPipSize } from '@trader/utils';
import { BuyButton } from '../../../components/buyButton';
import { OrderInput } from '../../../components/orderInput';
import { defaultIcon, small } from '@trader/constants';
import { TradingIsClosed, Wrapper } from '@trader/components';
import { useI18next } from '@trader/services';

interface IMarketOrder {
  freeMargin: number;
  addDeposit: () => void;
}

export const MarketOrder: React.FC<IMarketOrder> = ({
  freeMargin,
  addDeposit,
}) => {
  const { translate } = useI18next();
  const {
    instrument: {
      general,
      market: { amountVolume, minAmountValue, maxAmountValue, billType },
    },
    realTimePrice,
    errors,
    handleChangeMarket,
    isInsufficientError,
  } = useContext(TradingContext);

  const isPlaceMarketOrderDisable =
    !amountVolume ||
    !!errors.marketAmountError ||
    !!errors.stopLossError ||
    !!errors.takeProfitError ||
    !general.symbol ||
    !billType ||
    isInsufficientError;

  const handleChangeInput = (
    value: string,
    name: 'amountVolume' | 'valueVolume',
    variation?: TVariation
  ) => {
    handleChangeMarket(value, name, variation);
  };

  const spread = calculateSpread({
    ask: Number(realTimePrice.ask),
    bid: Number(realTimePrice.bid),
    pipSize: general.pipSize,
  });

  if (!general.isOpenTrading) {
    return (
      <Styled.Root>
        <TradingIsClosed
          title={translate('COMMON.LABELS.MARKETS_CLOSED')}
          iconTypeName='warning'
          content={`${translate(
            'COMMON.LABELS.MARKETS_ARE_CURRENTLY_CLOSED'
          )} ${
            !import.meta.env.VITE_IS_DMA
              ? translate('COMMON.LABELS.YOU_CAN_PLACE_A_LIMIT_STOP_ORDER')
              : ''
          }`}
        />
      </Styled.Root>
    );
  }

  return (
    <React.Fragment>
      {import.meta.env.VITE_IS_DMA && (
        <Styled.DmaHeader>
          <Wrapper>
            <img
              src={general?.iconUrl || defaultIcon}
              alt='instrument icon'
              width={small}
              height={small}
            />
            <Styled.Info>{translate('COMMON.LABELS.BUY_PRICE')}</Styled.Info>
          </Wrapper>
          <Styled.Price>
            {general.currencySymbol}
            {formatByPipSize(
              Number(realTimePrice.currentPrice),
              general.pipSize
            )}
          </Styled.Price>
        </Styled.DmaHeader>
      )}
      <Styled.Root>
        {!import.meta.env.VITE_IS_DMA && (
          <Styled.ActionButtons>
            <BuyButton order='Market' isAsk type='Sell' />
            <Styled.Spread>{spread}</Styled.Spread>
            <BuyButton order='Market' type='Buy' />
          </Styled.ActionButtons>
        )}
        <Styled.Main>
          <OrderInput
            placeholder={translate('COMMON.LABELS.AMOUNT')}
            disabled={!general.isOpenTrading}
            minValue={minAmountValue}
            maxValue={maxAmountValue}
            fixDigitAfterDot={general.fixDigitAfterDot}
            step={general.step}
            value={amountVolume}
            shouldCheckActivityOfButtons
            onChangeInput={(value, variation) => {
              handleChangeInput(value, 'amountVolume', variation);
            }}
            errorMsg={amountVolume && errors.marketAmountError}
          />
        </Styled.Main>
        <PurchaseDetails
          addDeposit={addDeposit}
          freeMargin={freeMargin}
          symbol={general.symbol}
          quantity={amountVolume}
          side={billType}
        />
      </Styled.Root>
      <Protection />
      {import.meta.env.VITE_IS_DMA && (
        <Styled.FreMargin>
          {translate('COMMON.LABELS.FREE_BALANCE')}: {general.currencySymbol}
          {freeMargin}
        </Styled.FreMargin>
      )}
      <PlaceOrderButton
        orderType='Market'
        isDisabled={isPlaceMarketOrderDisable}
      />
      {import.meta.env.VITE_INSTRUMENT_HAS_COST_AND_CHARGES && (
        <CostAndCharges />
      )}
    </React.Fragment>
  );
};
